import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{label}</Label>
    {children}
  </div>
);

export const BulkUnitsDialog = ({ open, onOpenChange, propertyId }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState({ prefijo: "Apt ", desde: 101, hasta: 400, piso: "", tipo: "apartamento" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () =>
      (
        await api.post("/units/bulk", {
          property_id: propertyId,
          prefijo: form.prefijo,
          desde: Number(form.desde),
          hasta: Number(form.hasta),
          piso: form.piso === "" ? null : Number(form.piso),
          tipo: form.tipo,
        })
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["property"] });
      toast.success(`${data.creadas} ${t("units.created")}, ${data.omitidas} ${t("units.skipped")}`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const count = Math.max(0, Number(form.hasta) - Number(form.desde) + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-units-dialog" className="max-w-[520px] rounded-[8px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("units.bulk_title")}</DialogTitle>
          <DialogDescription className="text-[13px]">{t("units.bulk_desc")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3.5">
          <Field label={t("units.prefix")}>
            <Input
              data-testid="bulk-prefix-input"
              value={form.prefijo}
              onChange={(e) => set("prefijo", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("units.type")}>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="bulk-type-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="apartamento">{t("units.types.apartamento")}</SelectItem>
                <SelectItem value="area_comun">{t("units.types.area_comun")}</SelectItem>
                <SelectItem value="exterior">{t("units.types.exterior")}</SelectItem>
                <SelectItem value="amenidad">{t("units.types.amenidad")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("units.from")}>
            <Input
              data-testid="bulk-from-input"
              type="number"
              value={form.desde}
              onChange={(e) => set("desde", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("units.to")}>
            <Input
              data-testid="bulk-to-input"
              type="number"
              value={form.hasta}
              onChange={(e) => set("hasta", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("units.floor")}>
            <Input
              data-testid="bulk-floor-input"
              type="number"
              value={form.piso}
              onChange={(e) => set("piso", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <div className="flex items-end">
            <p data-testid="bulk-preview" className="text-[12.5px] text-ink-tertiary">
              {count} unidades: <strong className="text-ink">{form.prefijo}{form.desde}</strong> …{" "}
              <strong className="text-ink">{form.prefijo}{form.hasta}</strong>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="bulk-cancel-button"
            className="h-9 rounded-[6px] text-[13px]"
            onClick={() => onOpenChange(false)}
          >
            {t("app.cancel")}
          </Button>
          <Button
            data-testid="bulk-save-button"
            disabled={count <= 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
          >
            {t("app.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
